import { Button, Flex, Image, Text, useToast, VStack } from '@chakra-ui/react'
import KalviumSdk from '@kalvi-education/kalvium-sdk'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { defaultBorder3 } from '../../../../chakraTheme'
import Toast from '../../../../common/Toast'
import { ArrowForward } from '../../../../components/Icons/default'
import useRazorpayCheckout from '../../hooks/useRazorpayCheckout'
import { loginUser } from '../../utils/login'
import { queryClient } from '../_queryClient'
import registrationSuccessfulImage from '../assets/registration-successful.gif'
import AuthTabs from '../common/AuthTabs'
import ProgramInfo from '../common/ProgramInfo'
import VerifyMobile from '../common/VerifyMobile'
import { REGISTER } from '../enums/authMode'
import { DIRECT, UNIVERSITY } from '../enums/clientType'
import { APPLICATION_FEES } from '../enums/paymentAmount'
import {
    BOUNCED,
    DROPPED,
    FAILURE,
    INITIATED,
    PRE_INITIATED,
    SUCCESS,
    USER_CANCELLED,
} from '../enums/paymentTxnStatus'
import { COUPON_CODE, TEST_CODE } from '../enums/voucherType'
import RegistrationForm from './components/Form'

const PAGE_MODE = {
    REGISTER: 'register',
    REGISTERED: 'registered',
}

const AdmissionClientRegister = () => {
    const { clientSlug } = useParams()
    const clientType = clientSlug === DIRECT ? DIRECT : UNIVERSITY
    const isDirectApplicant = clientType === DIRECT
    const { initiatePayment } = useRazorpayCheckout()
    const [pageMode, setPageMode] = useState(PAGE_MODE.REGISTER)
    const [paymentId, setPaymentId] = useState()
    const [voucherCode, setVoucherCode] = useState()
    const amount = APPLICATION_FEES
    const [mobile, setMobile] = useState('')
    const [otp, setOtp] = useState('')
    const [isOtpVerified, setIsOtpVerified] = useState(false)
    const toast = useToast()
    const navigate = useNavigate()
    const [isPaymentInProgress, setIsPaymentInProgress] = useState(false)

    const { data: payment } = useQuery({
        queryKey: ['payment', paymentId],
        queryFn: async queryData => {
            return KalviumSdk.services.admission.fetchPayment(
                queryData.queryKey[1]
            )
        },
        enabled: !!paymentId && pageMode === PAGE_MODE.REGISTER, // disabled as long as the paymentId is unavailable
        gcTime: 1000,
        refetchInterval: 1000,
    })

    const {
        data: voucher,
        refetch: fetchVoucherDetails,
        error: voucherError,
        isError: isVoucherError,
        isSuccess: isFetchVoucherSuccess,
    } = useQuery({
        queryKey: ['voucher', voucherCode],
        queryFn: async queryData => {
            return KalviumSdk.services.admission.fetchVoucher({
                code: queryData.queryKey[1],
                type: clientType === DIRECT ? COUPON_CODE : TEST_CODE,
            })
        },
        enabled: false,
        gcTime: 0,
        staleTime: 0,
        retry: false,
    })

    const {
        data: loginData,
        isError: isLoginError,
        error: loginError,
        isSuccess: isLoginSuccess,
        refetch: login,
    } = useQuery({
        queryKey: ['login', { otp: otp, mobile: mobile }],
        queryFn: async queryData => {
            return KalviumSdk.services.admission.login({
                mobile: queryData.queryKey[1].mobile,
                otp: queryData.queryKey[1].otp,
                clientSlug: clientSlug,
            })
        },
        enabled: false,
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    })

    useEffect(() => {
        if (isLoginSuccess) {
            loginUser(loginData.token)
            navigate('/admission/dashboard')
        }
    }, [isLoginSuccess])

    useEffect(() => {
        if (isLoginError) {
            toast({
                duration: 3000,
                position: 'top-right',
                render: props => (
                    <Toast
                        status={'error'}
                        title={loginError.message}
                        {...props}
                    />
                ),
            })
        }
    }, [isLoginError])

    useEffect(() => {
        if (payment?.status === SUCCESS) {
            setPageMode(PAGE_MODE.REGISTERED)
            setIsPaymentInProgress(false)
        } else if (
            [FAILURE, USER_CANCELLED, BOUNCED, DROPPED].includes(
                payment?.status
            )
        ) {
            toast({
                title: 'Looks like your previous transaction failed. Any money deducted would be refunded back to the source. Please try the transaction again',
                status: 'error',
                duration: 10000,
                isClosable: true,
                position: 'top-right',
                render: props => <Toast {...props} />,
            })
        }
    }, [payment])

    const onSubmit = async values => {
        try {
            setIsPaymentInProgress(true)
            const voucherType = clientType === DIRECT ? COUPON_CODE : TEST_CODE
            const resp = await KalviumSdk.services.admission.createPayment({
                voucherCode:
                    values.voucherCode !== '' && isFetchVoucherSuccess
                        ? values.voucherCode
                        : undefined,
                voucherType:
                    values.voucherCode !== '' && isFetchVoucherSuccess
                        ? voucherType
                        : undefined,
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                state: values.state,
                xiiYop: values.xiiYop,
                type: 'application-fees',
                mobile,
                otp,
                clientSlug,
            })

            let options = {
                order_id: resp.access_key,
                key: import.meta.env.VITE_RZP_KEY,
                prefill: {
                    contact: mobile,
                    name: `${values.firstName + ' ' + values.lastName} `,
                    email: values.email,
                },
                theme: {
                    color: '#6E5A8A',
                },
                modal: {
                    ondismiss: () => {
                        setPaymentId(undefined)
                        setIsPaymentInProgress(false)
                        toast({
                            title: 'Looks like your previous transaction failed. Any money deducted would be refunded back to the source. Please try the transaction again',
                            status: 'error',
                            duration: 10000,
                            isClosable: true,
                            position: 'top-right',
                            render: props => <Toast {...props} />,
                        })
                    },
                },
            }

            setPaymentId(resp.id)
            if (resp.status === INITIATED || resp.status === PRE_INITIATED) {
                initiatePayment(options)
            } else if (resp.status === SUCCESS) {
                setPageMode(PAGE_MODE.REGISTERED)
            } else {
                toast({
                    duration: 3000,
                    position: 'top-right',
                    render: props => (
                        <Toast
                            status={'error'}
                            title={'Cannot initiate payment'}
                            {...props}
                        />
                    ),
                })
                console.error(resp)
            }
        } catch (err) {
            console.error(err)
            setIsPaymentInProgress(false)
            toast({
                duration: 3000,
                position: 'top-right',
                render: props => (
                    <Toast status={'error'} title={err.message} {...props} />
                ),
            })
        }
    }

    const resetVoucher = () => {
        queryClient.resetQueries({
            queryKey: ['voucher'],
        })
    }

    return (
        <Flex justifyContent="center" flexGrow={1} h="full" zIndex={1}>
            <Flex
                flexDirection={{ base: 'column', lg: 'row' }}
                maxWidth={'1312px'}
                flexGrow={1}
            >
                <ProgramInfo clientSlug={clientSlug} />
                <Flex
                    flexDirection={'column'}
                    flexGrow={1}
                    bgColor={'white'}
                    overflowY={{ base: 'initial', lg: 'auto' }}
                    sx={{
                        '::-webkit-scrollbar': {
                            width: '4px',
                        },
                        '::-webkit-scrollbar-thumb': {
                            bg: 'wildsand2',
                        },
                    }}
                >
                    {pageMode === PAGE_MODE.REGISTER && (
                        <VStack
                            gap={0}
                            px={{ base: 4, md: 12, lg: 16 }}
                            pt={{ base: 4, md: 12 }}
                            alignItems={'stretch'}
                        >
                            <Flex
                                position={'sticky'}
                                zIndex={999}
                                top={{ base: '78px', lg: -1 }}
                            >
                                <AuthTabs
                                    activeTab={REGISTER}
                                    basePath={`/admission/${clientSlug}`}
                                />
                            </Flex>
                            {/* Mobile Verification */}
                            <Flex id="verify-mobile-wrapper">
                                <Flex
                                    pt={'72px'}
                                    id="verify-mobile-container"
                                    pb={12}
                                    borderBottom={defaultBorder3}
                                    flexGrow={1}
                                >
                                    <VerifyMobile
                                        title="Verify your mobile number to proceed"
                                        onOtpVerified={(_mobile, _otp) => {
                                            setMobile(_mobile)
                                            setOtp(_otp)
                                            setIsOtpVerified(true)
                                        }}
                                    />
                                </Flex>
                            </Flex>
                            {/* Registration Form */}
                            <RegistrationForm
                                onSubmit={onSubmit}
                                isDirectApplicant={isDirectApplicant}
                                amount={amount}
                                isOtpVerified={isOtpVerified}
                                setVoucherCode={setVoucherCode}
                                fetchVoucherDetails={fetchVoucherDetails}
                                voucher={voucher}
                                voucherError={voucherError}
                                isVoucherError={isVoucherError}
                                isFetchVoucherSuccess={isFetchVoucherSuccess}
                                clientType={clientType}
                                resetVoucher={resetVoucher}
                                isPaymentInProgress={isPaymentInProgress}
                            />
                        </VStack>
                    )}
                    {pageMode === PAGE_MODE.REGISTERED && (
                        <Flex
                            flexDirection={'column'}
                            px={8}
                            py={{ base: 120, lg: 0 }}
                            my={{ lg: 'auto' }}
                            alignItems={'center'}
                        >
                            <Image
                                src={registrationSuccessfulImage}
                                w={220}
                                h={180}
                            />
                            <Text mt={6} textAlign={'center'} maxW={350}>
                                You have successfully registered for the KQ
                                Assessment.
                            </Text>
                            <Button variant="clickable" mt={8} onClick={login}>
                                Login to Dashboard
                                <ArrowForward ml={3} fontSize={'2xl'} />
                            </Button>
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </Flex>
    )
}

export default AdmissionClientRegister
